import type { SelectProps } from "antd";
import { Select, Tag } from "antd";
import type { FC } from "react";
import React, { useEffect, useState } from "react";

import type { LabeledValue } from "antd/es/select";

import type { LiteralUnion } from "antd/es/_util/type";
import type {
  PresetColorType,
  PresetStatusColorType,
} from "antd/es/_util/colors";
import {
  Core_KnowledgeInputMsg,
  Input_Knowledge_Facet,
  Knowledge_KnowledgeDataset,
  Knowledge_SearchLanguage,
  Knowledge_SearchResult,
  useKnowledgeBaseLazyQuery,
} from "../../src/generated/graphql";
import { Maybe } from "@graphql-tools/utils/typings/types";
import { useIntl } from "react-intl";
import { AveliosMultiSelect } from "./avelios-multi-select";

interface KnowledgeBaseMultiInputProps
  extends SelectProps<Core_KnowledgeInputMsg>,
    React.RefAttributes<any> {
  values: (Core_KnowledgeInputMsg | null)[] | undefined | null;
  dataset: Knowledge_KnowledgeDataset;
  setValues: (inputMsgs?: Maybe<Maybe<Core_KnowledgeInputMsg>[]>) => void;
  placeholder?: string;
  clinicId: Maybe<string> | undefined;
  loading?: boolean;
  disabled?: boolean;
  displayMode?: boolean;
  tagColor?: LiteralUnion<PresetColorType | PresetStatusColorType, string>;
  allowCustomTags?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  facets?: [Input_Knowledge_Facet];
  showDropdownOnKeyDown?: boolean;
}

export interface VeiosInputLabel extends LabeledValue {
  id: string;
  generalId: string;
  key: string;
}

export const GeneralKnowledgeBaseMultiInput: FC<
  KnowledgeBaseMultiInputProps
> = ({
  dropdownMatchSelectWidth = true,
  allowCustomTags = true,
  displayMode = false,
  tagColor = "blue",
  values,
  dataset,
  setValues,
  placeholder,
  loading,
  disabled,
  onKeyDown,
  facets,
  clinicId,
  showDropdownOnKeyDown,
  ...props
}) => {
  const { Option } = Select;
  const { formatMessage } = useIntl();
  const [value, setValue] = useState<VeiosInputLabel[]>();
  const [options, setOptions] = useState<Maybe<Knowledge_SearchResult>[]>([]);
  const [getKnowledge, { data: knowledgeData }] = useKnowledgeBaseLazyQuery();
  const [customValue, setCustomValue] = useState<string>("");

  useEffect(() => {
    if (knowledgeData?.searchStandardizedData?.results !== undefined) {
      setOptions(knowledgeData?.searchStandardizedData?.results ?? []);
    }
  }, [knowledgeData?.searchStandardizedData?.results]);

  function resetKnowledge() {
    if (clinicId) {
      getKnowledge({
        variables: {
          request: {
            searchTerm: "",
            dataset,
            language: Knowledge_SearchLanguage.SearchLanguageDe,
            facets,
          },
        },
      });
    }
  }

  useEffect(() => {
    resetKnowledge();
  }, []);

  function getDisplayValue() {
    return value && value.length > 0 ? (
      <>
        {value.map(
          (val) =>
            val?.label && (
              <Tag key={val.key} color={tagColor}>
                {val.label}
              </Tag>
            )
        )}
      </>
    ) : (
      <>-</>
    );
  }

  const [open, setOpen] = useState<boolean>();

  function getOptions() {
    const res = [];

    const other: JSX.Element[] = [];
    options.forEach((option) => {
      other.push(
        <Option
          key={option!.text! + option!.generalId}
          value={option!.text! + option!.generalId}
          generalId={option!.generalId}
        >
          {option?.text}
        </Option>
      );
      option!.configurations?.forEach((config) => {
        other.push(
          <Option
            key={`${option?.text} ${config}${option!.generalId}`}
            value={`${option?.text} ${config}${option!.generalId}`}
            generalId={option!.generalId}
          >
            {`${option?.text} ${config}`}
          </Option>
        );
      });
    });
    if (
      allowCustomTags &&
      customValue !== "" &&
      options.find((o) => o?.text === customValue) === undefined
    ) {
      other.push(
        <Option key={customValue} value={customValue} generalId={""}>
          {customValue}
        </Option>
      );
    }

    res.push(other);

    return res;
  }

  return !displayMode ? (
    <AveliosMultiSelect
      getKey={(v) => v.key}
      dropdownMatchSelectWidth={dropdownMatchSelectWidth}
      onBlur={() => {
        setOpen(false);
      }}
      onFocus={() => {
        setOpen(true);
      }}
      onKeyDown={(e) => {
        if (customValue === "" && onKeyDown) {
          onKeyDown(e);
        }
      }}
      open={open}
      disabled={disabled}
      showSearch
      allowClear
      value={value}
      // @ts-ignore
      onChange={(v, option) => {
        setValues(
          // @ts-ignore
          option.map((item) => ({
            text: item.children,
            generalId: item.generalId,
          }))
        );
      }}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(true);
      }}
      placeholder={
        placeholder ?? formatMessage({ id: "knowledge-input.select" })
      }
      onClear={() => {
        setValues([]);
        setCustomValue("");
      }}
      onSearch={(v) => {
        if (clinicId) {
          setCustomValue(v);
          if (!open && showDropdownOnKeyDown) {
            setOpen(true);
          }
          getKnowledge({
            variables: {
              request: {
                searchTerm: v,
                dataset,
                language: Knowledge_SearchLanguage.SearchLanguageDe,
                facets,
              },
            },
          });
        }
      }}
      mode={"multiple"}
      style={{ width: "100%" }}
      filterOption={false}
      labelInValue
      loading={loading}
      {...props}
    >
      {getOptions()}
    </AveliosMultiSelect>
  ) : (
    getDisplayValue()
  );
};
